import { useState } from "react";

import { useApprove, useReject, useRequeue, type QuestionDetail } from "@/api/queries";
import { PipelineNudge } from "@/components/pipeline-nudge";
import { Button } from "@/components/ui/button";
import { FieldLabel, Input } from "@/components/ui/input";
import { ErrorNote } from "@/components/ui/panel";

/**
 * Statuses `review.approve` accepts (src/qbank/orchestration/review.py `_APPROVABLE`).
 * Duplicated here rather than round-tripped through `/config`: it is a UI hint that
 * saves a doomed request, not the enforcement — the API 400s regardless.
 */
const APPROVABLE = new Set(["pending_review", "stale", "rejected"]);

/**
 * Approve/reject/requeue for a question the Review queue no longer shows.
 *
 * Review only ever lists `pending_review`/`stale` rows, so it never needed a way to
 * reverse a decision once a question left it. Questions is the only screen that can
 * still reach a decided row — this is what turns "I rejected that by mistake" into a
 * second click instead of a dead end.
 */
export function QuestionStatusActions({ q }: { q: QuestionDetail }) {
  const [note, setNote] = useState("");
  const [nudge, setNudge] = useState<string | null>(null); // the stage the row landed on
  const approve = useApprove();
  const reject = useReject();
  const requeue = useRequeue();
  const busy = approve.isPending || reject.isPending || requeue.isPending;

  function act(run: () => void) {
    run();
    setNote("");
  }

  /** Requeue (auto-detected stage, or `stage` forced — "extracted" re-enriches). */
  function doRequeue(stage?: string) {
    requeue.mutate({ id: q.id, note, stage }, { onSuccess: (result) => setNudge(result.status) });
    setNote("");
  }

  return (
    <div className="border-bank-border space-y-2 border-t pt-3">
      <FieldLabel htmlFor="status-note">Note — recorded on the status_transitions row</FieldLabel>
      <Input
        id="status-note"
        value={note}
        placeholder="why, for whoever reads this in six months"
        onChange={(e) => setNote(e.target.value)}
      />
      <div className="flex flex-wrap gap-1.5">
        {APPROVABLE.has(q.status) && (
          <Button
            size="sm"
            variant="primary"
            disabled={busy}
            onClick={() => act(() => approve.mutate({ id: q.id, note }))}
          >
            Approve
          </Button>
        )}
        {q.status !== "rejected" && (
          <Button
            size="sm"
            variant="danger"
            disabled={busy}
            onClick={() => act(() => reject.mutate({ id: q.id, note }))}
          >
            Reject
          </Button>
        )}
        <Button size="sm" disabled={busy} onClick={() => doRequeue()}>
          Requeue
        </Button>
        <Button
          size="sm"
          disabled={busy}
          title="Force a fresh enrichment pass, even though this row already has one"
          onClick={() => doRequeue("extracted")}
        >
          Re-enrich
        </Button>
      </div>
      {nudge && <PipelineNudge stage={nudge} onDismiss={() => setNudge(null)} />}
      <ErrorNote error={approve.error ?? reject.error ?? requeue.error} />
    </div>
  );
}
